import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import type { RootState } from "../rootReducer";

export type FieldTeamRole = "field_technician" | "admin";

export interface FieldTeamMember {
  id: string;
  name: string;
  email: string;
  role: FieldTeamRole;
}

export interface AuthState {
  isAuthenticated: boolean;
  fieldTeamMember: FieldTeamMember | null;
  token: string | null;
}

const name = "Auth";

export const initialState: AuthState = {
  isAuthenticated: false,
  fieldTeamMember: null,
  token: null,
};

export const authSlice = createSlice({
  name,
  initialState,
  reducers: {
    setAuth: (
      state,
      action: PayloadAction<{
        fieldTeamMember: FieldTeamMember;
        token: string;
      }>
    ) => {
      state.isAuthenticated = true;
      state.fieldTeamMember = action.payload.fieldTeamMember;
      state.token = action.payload.token;
    },
    clearAuth: () => initialState,
    /** Latest Id token from Amplify or native `cognitoTokens()` (after refresh). */
    updateIdToken: (state, action: PayloadAction<string>) => {
      if (state.isAuthenticated && action.payload) {
        state.token = action.payload;
      }
    },
  },
});

export const { setAuth, clearAuth, updateIdToken } = authSlice.actions;

export const selectIsAuthenticated = (state: RootState): boolean =>
  state.auth.isAuthenticated;

export const selectFieldTeamMember = (state: RootState) =>
  state.auth.fieldTeamMember;

export const selectToken = (state: RootState): string | null =>
  state.auth.token;

const persistedAuthReducer = persistReducer(
  {
    key: "auth",
    storage,
    whitelist: ["isAuthenticated", "fieldTeamMember", "token"],
  },
  authSlice.reducer
);

export default persistedAuthReducer;
